import React, { useCallback, useState } from 'react';

import { Text } from '@rbilabs/universal-components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import {
  initialErrorState,
  initialPaymentState,
  testCardPaymentState,
} from 'features/payment/initial-state';
import { CardType } from 'features/payment/types';
import {
  getCCFormErrors,
  mapDeliveryToCCFormAddress,
  mapPaymentMethodCardTypes,
  onCCFormChange,
} from 'features/payment/utils';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import CreditCardFormInputs, { getErrorMessage } from 'components/credit-card-form-inputs';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { SafetechEncryption } from 'components/payments/integrations/orbital/components/encryption';
import {
  IEncryptionError,
  IEncryptionResult,
} from 'components/payments/integrations/orbital/components/encryption/types';
import { toaster } from 'components/toaster/toaster';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { usePaymentCheckoutRedesignExperiment } from 'experiments/use-payment-checkout-redesign-experiment';
import { useValidateCCNumberInClientExperiment } from 'experiments/use-validate-cc-number-in-client';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  SupportedCardType,
  defaultPaymentFieldVariation,
  defaultSupportedCardTypes,
} from 'state/launchdarkly/variations';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useOrderContext } from 'state/order';
import logger from 'utils/logger';
import { stringToBool } from 'utils/string-To-Bool';

export const AddPaymentMethodModalRedesign: React.FC = () => {
  const { user } = useAuthContext();
  const [errors, setErrors] = useState(initialErrorState);
  const [isAddEnabled, setIsAddEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { loading: isUserLoading } = useLoyaltyUserId();
  const router = useRouter();

  const {
    addPaymentMethod,
    transformPaymentValues,
    isOrbital,
    setEncryptionResult,
    isQuickPayMethodSelected,
    setIsQuickPayMethodSelected,
    setIsAddPaymentMethodSuccess,
    storePaymentDetails,
    setCheckoutPaymentMethodId,
    accountToDelete,
    setAccountToDelete,
  } = usePaymentContext();
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const isPaymentRedesignEnabled = usePaymentCheckoutRedesignExperiment();

  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];

  const { deliveryAddress, isDelivery: isDeliveryServiceMode } = useOrderContext();

  const isDelivery =
    stringToBool(useLocalSearchParams<{ isInCart: string }>().isInCart) && isDeliveryServiceMode;
  const { feCountryCode: billingCountry } = useLocale();

  const userDetailsName = user?.details.name;

  const [paymentValues, setPaymentValues] = useState(
    initialPaymentState({
      isDelivery,
      deliveryAddress,
      billingCountry,
      userDetailsName,
    })
  );
  const { formatMessage } = useIntl();
  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Credit Card details',
  });
  const [isEncryptionActive, setIsEncryptionActive] = useState(false);

  const validateCCExperiment = useValidateCCNumberInClientExperiment();

  useEffectOnce(() => {
    if (autoFill) {
      setPaymentValues(testCardPaymentState({ isDelivery, paymentFieldVariations }));
    }
  });

  const handleChange = useCallback(
    (name: any, value: any) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        paymentValues,
        errors,
        formatMessage,
        supportedCardTypes,
        validateCCExperiment
      );
      if ((isPaymentRedesignEnabled && value !== 'false') || !isPaymentRedesignEnabled) {
        setErrors(prevState => ({ ...prevState, ...formErrors }));
      }
      setPaymentValues(prevState => {
        return state.billingAddressSameAsDelivery
          ? {
              ...prevState,
              ...state,
              ...mapDeliveryToCCFormAddress(deliveryAddress),
            }
          : { ...prevState, ...state };
      });
    },
    [
      isPaymentRedesignEnabled,
      paymentValues,
      errors,
      formatMessage,
      deliveryAddress,
      supportedCardTypes,
      validateCCExperiment,
    ]
  );

  const addNewCard = useCallback(
    async (event: any, encryptionResult?: IEncryptionResult) => {
      if (event) {
        event.preventDefault();
      }

      const onAddCardSuccess = () => {
        // Showing notifications
        toaster.positive(
          accountToDelete
            ? formatMessage({ id: 'cardUpdatedNotification' })
            : formatMessage({ id: 'cardAddedNotification' })
        );
        setPaymentValues(initialPaymentState({ isDelivery, deliveryAddress }));
        if (isQuickPayMethodSelected) {
          setIsAddPaymentMethodSuccess(true);
          setIsQuickPayMethodSelected(false);
        }

        router.dismiss();

        setIsLoading(false);
      };

      const onAddCardFailure = (paymentMethodError: Error, message: string) => {
        logger.error({ error: paymentMethodError, message });
        openErrorDialog({
          message: formatMessage({ id: 'paymentAddingError' }),
          error: paymentMethodError,
        });
        setIsLoading(false);
      };

      setIsLoading(true);
      const reshapedPaymentValues = transformPaymentValues({
        paymentValues,
        paymentFieldVariations,
      });
      reshapedPaymentValues.accountToDelete = accountToDelete || '';

      if (isPaymentRedesignEnabled && !paymentValues.saveCard) {
        // The automatic selection of this method is performed in the initPaymentMethods
        // function within the usePayment hook.
        storePaymentDetails({ state: paymentValues });
        setCheckoutPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_CARD);
        onAddCardSuccess();
        setAccountToDelete('');
        return;
      }

      try {
        await addPaymentMethod(
          reshapedPaymentValues,
          {
            skipErrorDialogOnError: true,
          },
          encryptionResult
        );

        onAddCardSuccess();
        setAccountToDelete('');
      } catch (error) {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        onAddCardFailure(error, 'Error adding payment method');
      }
    },
    [
      transformPaymentValues,
      paymentValues,
      paymentFieldVariations,
      accountToDelete,
      isPaymentRedesignEnabled,
      formatMessage,
      isDelivery,
      deliveryAddress,
      isQuickPayMethodSelected,
      router,
      setIsAddPaymentMethodSuccess,
      setIsQuickPayMethodSelected,
      openErrorDialog,
      storePaymentDetails,
      setCheckoutPaymentMethodId,
      setAccountToDelete,
      addPaymentMethod,
    ]
  );

  const handleOnPress = useCallback(
    async (event: any) => {
      if (event) {
        event.preventDefault();
      }
      const { hasErrors, formErrors } = getCCFormErrors(
        paymentValues,
        errors,
        formatMessage,
        paymentFieldVariations,
        billingCountry
      );

      if (hasErrors) {
        // TODO: RN - Focus on first field with errors
        const newErrorMessage = getErrorMessage({ errors, formatMessage });
        setErrorMessage(newErrorMessage);

        return setErrors(prevState => ({ ...prevState, ...formErrors, didAttemptSubmit: true }));
      }

      if (isOrbital) {
        setEncryptionResult(undefined);
        setIsLoading(true);
        return setIsEncryptionActive(true);
      }
      addNewCard(null);
    },
    [
      addNewCard,
      billingCountry,
      errors,
      formatMessage,
      isOrbital,
      paymentFieldVariations,
      paymentValues,
      setEncryptionResult,
    ]
  );

  const onEncryptionResult = (data: IEncryptionResult) => {
    setEncryptionResult(data);
    setIsEncryptionActive(false);
    addNewCard(null, data);
  };

  // Handle encryption error
  const onEncryptionError = (error: IEncryptionError) => {
    setIsEncryptionActive(false);
    setEncryptionResult(undefined);
    setIsLoading(false);
    const errorMessage = error.error;
    logger.error({ error: new Error(errorMessage), message: errorMessage });
    openErrorDialog({
      message: formatMessage({ id: 'paymentAddingError' }),
      error: new Error(errorMessage),
    });
  };

  return (
    <CardModal
      standardPadding
      scrollView
      onDismiss={() => {
        if (accountToDelete) {
          setAccountToDelete('');
        }
        router.dismiss();
      }}
      testID="add-payment-modal-heading"
      headerText={
        accountToDelete
          ? formatMessage({ id: 'updateCard' })
          : formatMessage({ id: 'addCreditDebitCard' })
      }
      footer={
        <ActionButton
          fullWidth
          onPress={handleOnPress}
          disabled={!!isAddEnabled}
          isLoading={isLoading || isUserLoading}
        >
          {accountToDelete ? formatMessage({ id: 'update' }) : formatMessage({ id: 'addToCart' })}
        </ActionButton>
      }
    >
      {!!errorMessage && (
        <VisuallyHidden role="alert" accessibilityLabel="">
          <Text>{errorMessage}</Text>
        </VisuallyHidden>
      )}
      <CreditCardFormInputs
        onChange={handleChange}
        paymentValues={paymentValues}
        errors={errors}
        isDelivery={isDelivery}
        setIsAddEnabled={setIsAddEnabled}
        isPrepaidReload={false}
        isUpdate={!!accountToDelete}
        isQuickPayMethodSelected={isQuickPayMethodSelected}
      />
      <ErrorDialog />
      {isOrbital && isEncryptionActive ? (
        <SafetechEncryption
          cardNumber={paymentValues.cardNumber}
          cvv={paymentValues.cvv || ''}
          onResult={onEncryptionResult}
          onError={onEncryptionError}
        />
      ) : null}
    </CardModal>
  );
};
